import { Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators, FormArray } from '@angular/forms';
import { ServiceRouter } from '../../../../services/core/http-service-router.service';
import { Options } from 'ngx-slider-v2';
import { TIME_SLIDER_OPTIONS } from '../../../../constants/ng-slider.constants';
import { METHOD_TYPE } from 'src/app/constants/service.constants';
import { SpinnerComponent } from '../../../shared/spinner/spinner.component';
import { DatePipe } from '@angular/common';
import { Environment } from '../../../../config/environment';
import { DataCarrierService } from '../../../../services/core/data-carrier.service';
import { Config } from '../../../../config/config';
import { Router } from '@angular/router';
import { MatStepper } from '@angular/material/stepper';
import { ModalService } from '../../../../services/core/modal.service';
import { MatDialog } from '@angular/material/dialog';
import { AddLocationComponent } from '../add-location/add-location.component';
import { AddAddressService } from '../../../../services/scheduling/add-address/add-address.service';
import { ValidationUtilityService } from '../../../shared/utils/validation.util/validation-utility.service';
import { AnalyticsBean } from 'src/app/beans/analytics/analytics-module.bean';
import { Subscription } from 'rxjs';
import { EheAnalyticsDirective } from 'src/app/modules/shared/ehe-analytics/ehe-analytics.directive';

/**
 * Specify preferences component
 */
@Component({
	selector: 'app-ehe-specify-preferences',
	templateUrl: './specify-preferences.component.html',
	styleUrls: ['./specify-preferences.component.scss'],
})
export class SpecifyPreferencesComponent implements OnInit, OnChanges, OnDestroy {
	/** Plan name array */
	planName = [];
	encodedData: any;
	/** Plan type array */
	planType = [];
	/** Provider choice - EHE or own provider */
	providerChoice: any;
	/** Profile address */
	address: any;
	/** Location selected boolean flag */
	locationSelected: any;
	/** Location object */
	location: any = { city: '', state: '', zip: '' };
	/** Data carrier subscriptions */
	subscriptions: Subscription[] = [];
	/** Location array for adding new preferred locations */
	locationArr = [];
	/** Appointment time slider */
	sliderTime = [];
	/** Profile city */
	city: any;
	/** Profile state */
	state: any;
	/** Profile zip */
	zip: any;
	/** States array in add location dialog */
	states: string[] = [];
	/** Specify preferences form */
	specifyPreferencesForm: FormGroup;
	/** Request referral appointment preferences details */
	@Input() requestReferralDetails: any = { appt_prefs: {} };
	/** Appointment time slider value */
	value = TIME_SLIDER_OPTIONS.VALUE;
	/** Appointment time slider high value */
	highValue = TIME_SLIDER_OPTIONS.HIGH_VALUE;
	/** Appointment time slider options */
	options: Options = TIME_SLIDER_OPTIONS.OPTIONS;
	/** Appointment coordination flag */
	disabled: boolean;
	/** Referral top stepper */
	@Input() stepper: MatStepper;
	/** Request referral details reset output emitter */
	@Output() resetEvent = new EventEmitter<any>();
	/** Appointment preferences date picker min date */
	minDate = new Date();
	/** Show other insurance plan name flag */
	showPlanNameOther: boolean = false;
	/** Show other dental plan name flag */
	showDentalNameOther: boolean = false;
	/** Show other vision plan name flag */
	showVisionNameOther: boolean = false;
	/** Show other insurance plan type flag */
	showPlanTypeOther: boolean = false;
	/** Show other dental plan type flag */
	showDentalTypeOther: boolean = false;
	/** 3 Time slider arrays */
	sliderArr = [0, 1, 2];
	/** Can add new location flag */
	canAddNewLocation: boolean = true;
	/** Analytics bean */
	analyticsData: AnalyticsBean;
	/** Medical filter */
	medicalFilter = { medicalGroup: 2, status: '1' };
	/** Dental filter */
	dentalFilter = { medicalGroup: 1, status: '1' };
	/** Vision filter */
	visionFilter = { medicalGroup: 3, status: '1' };
	/**
	 * Specify preferences constructor
	 * @param serviceRouter service router
	 * @param spinner spinner component
	 * @param environment environment file that supplies env values
	 * @param dataCarrier data carrier service
	 * @param config config file that holds the service end points
	 * @param route angular router
	 * @param modalService generic modal service
	 * @param dialog material dialog service
	 * @param addAddressService Add address service
	 * @param eheAnalyticsDirective Analytics directive
	 */
	constructor(
		private serviceRouter: ServiceRouter,
		private spinner: SpinnerComponent,
		private environment: Environment,
		private dataCarrier: DataCarrierService,
		private config: Config,
		public route: Router,
		private modalService: ModalService,
		public dialog: MatDialog,
		private addAddressService: AddAddressService,
		private eheAnalyticsDirective: EheAnalyticsDirective
	) {
		this.analyticsData = new AnalyticsBean();
		this.analyticsData.componentName = 'specify-preferences';
		this.analyticsData.pageUrl = '/wellness/referral/request';
	}

	/**
	 * Component change detection and form updation
	 */
	ngOnChanges(changes) {
		if (changes.requestReferralDetails.currentValue !== undefined) {
			this.handleRequestReferralObjectChange(changes.requestReferralDetails.currentValue);
		}
	}

	/**
	 * Component initialization - fetches profile, states, location, insurance plan and type information
	 * Generates specify preferences form
	 * Calls to check appointment preferences and insurance selection for loading form accordingly
	 */
	ngOnInit() {
		this.canAddNewLocation = true;
		this.getStates();
		this.subscriptions.push(
			this.dataCarrier.getChannel().subscribe(data => {
				const profile = data.profile.profileInfo;
				if (profile) {
					this.address = profile.address;
					this.city = profile.city;
					this.state = profile.state;
					this.zip = profile.zipCode;
				}
			})
		);

		this.generateSpecifyPreferenceForm();
		this.getInsuranceTypes();
		this.getInsurancePlans();
		this.checkAppointmentCoordination();
		this.checkInsuranceSelection();
		this.locationGroup({
			city: this.city,
			state: this.state,
			zip: this.zip,
			locationSelected: true,
		});
	}

	/**
	 * Checks if appointment coordination from EHE is required
	 * If yes enables date and time picker for scheduling appointments else disables
	 */
	checkAppointmentCoordination() {
		this.subscriptions.push(
			this.specifyPreferencesForm.get('apptCordinationRequired').valueChanges.subscribe(value => {
				if (value === '1') {
					this.specifyPreferencesForm.get('date_time').enable();
					this.specifyPreferencesForm.get('transferEheRecords').enable();
				} else {
					this.specifyPreferencesForm.get('date_time').disable();
					this.specifyPreferencesForm.get('transferEheRecords').disable();
				}
			})
		);
	}

	/**
	 * Checks insurance plan selection and shows/hide fields 'Insurance Plan Name Other',
	 * 'Dental Plan Name Other' and 'Vision Plan Name Other'
	 * for the user to enter details other than the entries available in the dropdown
	 */
	checkInsuranceSelection() {
		this.subscriptions.push(
			this.specifyPreferencesForm.get('insurance').valueChanges.subscribe(value => {
				if (value.planId || value.dentalPlanId || value.visionPlanId) {
					this.showPlanNameOther = false;
					this.showDentalNameOther = false;
					this.showVisionNameOther = false;
					if (this.planName) {
						this.planName.forEach(plan => {
							if (plan.name === 'Unknown') {
								if (plan.id === value.planId && plan.medicalGroup === this.medicalFilter.medicalGroup) {
									this.showPlanNameOther = true;
								}
								if (plan.id === value.dentalPlanId && plan.medicalGroup === this.dentalFilter.medicalGroup) {
									this.showDentalNameOther = true;
								}
								if (plan.id === value.visionPlanId && plan.medicalGroup === this.visionFilter.medicalGroup) {
									this.showVisionNameOther = true;
								}
							}
						});
					}
				}
				if (value.planTypeId || value.dentalPlanTypeId) {
					this.showPlanTypeOther = false;
					this.showDentalTypeOther = false;
					if (this.planType) {
						this.planType.forEach(type => {
							if (type.name === 'Unknown') {
								if (type.id === value.planTypeId && type.medicalGroup === this.medicalFilter.medicalGroup) {
									this.showPlanTypeOther = true;
								}
								if (type.id === value.dentalPlanTypeId && type.medicalGroup === this.dentalFilter.medicalGroup) {
									this.showDentalTypeOther = true;
								}
							}
						});
					}
				}
			})
		);
	}

	/**
	 * Retrieves list of states for add location modal
	 */
	getStates() {
		this.spinner.startLoader();
		this.subscriptions.push(
			this.addAddressService.getStates().subscribe(
				(res: any) => {
					this.spinner.stopLoader();
					res.forEach(state => this.states.push(state));
				},
				err => {
					// Error
					this.spinner.stopLoader();
				}
			)
		);
	}

	/**
	 * Specify preferences form generation
	 */
	generateSpecifyPreferenceForm() {
		this.specifyPreferencesForm = new FormGroup({
			location: new FormArray([]),
			genderPref: new FormControl('', Validators.compose([Validators.required])),
			apptCordinationRequired: new FormControl('', Validators.compose([Validators.required])),
			date_time: this.dateFormGroup(),
			notes: new FormControl('', Validators.compose([Validators.required])),
			transferEheRecords: new FormControl(false, Validators.compose([Validators.required])),
			patientConsentProvided: new FormControl(false, Validators.compose([Validators.requiredTrue])),
			insurance: new FormGroup({
				planId: new FormControl('', Validators.compose([Validators.required])),
				planTypeId: new FormControl('', Validators.compose([Validators.required])),
				planNameOther: new FormControl(''),
				planTypeOther: new FormControl(''),
				dentalPlanId: new FormControl(''),
				dentalPlanTypeId: new FormControl(''),
				dentalPlanNameOther: new FormControl(''),
				dentalPlanTypeOther: new FormControl(''),
				visionPlanId: new FormControl(''),
				visionPlanNameOther: new FormControl(''),
				memberId: new FormControl(
					'',
					Validators.compose([Validators.required, ValidationUtilityService.groupNumberValidator])
				),
				groupNumber: new FormControl(
					'',
					Validators.compose([Validators.required, ValidationUtilityService.memberIdValidator])
				),
				outOfNetwork: new FormControl(false),
			}),
		});
	}

	/**
	 * Grouping locations added in location dropdown
	 * Can add only upto 4 location preferences
	 * @param location
	 */
	locationGroup(location) {
		const locationFormArr = <FormArray>this.specifyPreferencesForm.get('location');
		// if (!location) {
		//   location = {
		//     city: '',
		//     state: '',
		//     zip: ''
		//   };
		// }
		locationFormArr.push(
			new FormGroup({
				city: new FormControl(location.city),
				state: new FormControl(location.state),
				zip: new FormControl(location.zip),
				locationSelected: new FormControl(true),
			})
		);
		this.locationArr.push(location);
		if (this.locationArr.length >= 4) {
			this.canAddNewLocation = false;
		}
	}

	/**
	 * Remove a location preference that was added
	 * @param index
	 */
	removeLocationGroup(index) {
		const location = <FormArray>this.specifyPreferencesForm.get('location');
		location.removeAt(index);
	}

	dateFormGroup() {
		const dateFormArr = new FormArray([]);
		for (const i of this.sliderArr) {
			dateFormArr.push(
				new FormGroup({
					date: new FormControl(''),
					fromTime: new FormControl(''),
					toTime: new FormControl(''),
				})
			);
			this.sliderTime.push({ value: this.value, highValue: this.highValue });
		}
		return dateFormArr;
	}
	//        date: new FormControl('', Validators.compose([Validators.required]))

	/**
	 * whenever user changing the entry on the previous section (add details)
	 * the change will update to this.
	 * @param currentValue latest value from add details section
	 */
	handleRequestReferralObjectChange(currentValue) {
		this.requestReferralDetails = currentValue;
		if (this.requestReferralDetails.referral !== undefined) {
			this.requestReferralDetails.referral.forEach(referral => {
				if (referral.providerChoice === '2') {
					this.disabled = true;

					/**
					 *  if user selects their own provider
					 *  set the appointment coordination required flag,
					 *  transfer ehe records flag
					 *  and disable date
					 */
					this.specifyPreferencesForm.get('apptCordinationRequired').setValue('1', { emitEvent: false });
					// this.specifyPreferencesForm.get('transferEheRecords').setValue(true, { emitEvent: false });
					// this.specifyPreferencesForm.get('date_time').disable();
				} else {
					/**
					 *  if user allows to EHE to select provider
					 *  set the appointment coordination required flag,
					 *  transfer ehe records flag
					 *  and disable date
					 */
					this.specifyPreferencesForm.get('apptCordinationRequired').setValue('', { emitEvent: false });
					// this.specifyPreferencesForm.get('transferEheRecords').setValue(false, { emitEvent: false });
					this.specifyPreferencesForm.get('date_time').enable();
					this.disabled = false;
				}
			});
		}
	}

	/**
	 * Fetches list of insurance plans
	 */
	getInsurancePlans() {
		this.spinner.startLoader();
		this.subscriptions.push(
			this.serviceRouter
				.makeRequest(
					this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
					this.config.url.referralApi.getInsurancePlans,
					METHOD_TYPE.SECURED_GET,
					''
				)
				.subscribe(
					response => {
						this.spinner.stopLoader();
						if (response.insurance_plan) {
							this.planName = response.insurance_plan;
						}
					},
					error => {
						this.spinner.stopLoader();
					}
				)
		);
	}

	/**
	 * Fetches list of insurance types
	 */
	getInsuranceTypes() {
		this.spinner.startLoader();
		this.subscriptions.push(
			this.serviceRouter
				.makeRequest(
					this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
					this.config.url.referralApi.getInsuranceTypes,
					METHOD_TYPE.SECURED_GET,
					''
				)
				.subscribe(
					response => {
						this.spinner.stopLoader();
						if (response.insurance_type) {
							this.planType = response.insurance_type;
						}
					},
					error => {
						this.spinner.stopLoader();
					}
				)
		);
	}

	/**
	 * Groups added date and time preferences for final bean
	 * @param specifyPreferencesBean
	 */
	validateDateTime(specifyPreferencesBean) {
		const dateTimeList = [];
		let count = 0;
		const newList = [];

		for (const tempDate of specifyPreferencesBean.date_time) {
			tempDate.date = new DatePipe('en-US').transform(tempDate.date, 'MM/dd/yyyy');
			tempDate.fromTime = this.translate(this.sliderTime[count].value);
			tempDate.toTime = this.translate(this.sliderTime[count].highValue);
			if (tempDate.date !== null) {
				dateTimeList.push(tempDate);
			}
			count += 1;
		}

		if (dateTimeList.length > 0) {
			this.specifyPreferencesForm.get('date_time').setErrors(null, { emitEvent: false });
		} else {
			this.specifyPreferencesForm.get('date_time').setErrors({ atleastOne: true }, { emitEvent: false });
		}
		return dateTimeList;
	}

	/**
	 * Groups selected location preferences for final bean
	 * @param specifyPreferencesBean
	 */
	validateLocations(specifyPreferencesBean) {
		const locList = [];
		for (const tempLoc of specifyPreferencesBean.location) {
			if (tempLoc.locationSelected === true) {
				locList.push({
					city: tempLoc.city,
					state: tempLoc.state,
					zip: tempLoc.zip || 94588,
				});
			}
		}

		if (locList.length > 0) {
			this.specifyPreferencesForm.get('location').setErrors(null, { emitEvent: false });
		} else {
			this.specifyPreferencesForm.get('location').setErrors({ atleastOne: true }, { emitEvent: false });
		}
		return locList;
	}

	/**
	 * On submit, deletes unwanted bean objects from final bean
	 * @param specifyPreferencesBean
	 */
	removeValidations(specifyPreferencesBean) {
		this.requestReferralDetails.appt_prefs = specifyPreferencesBean;

		this.requestReferralDetails.referral.forEach(referral => {
			if (referral.providerChoice === '2') {
				delete referral.providerChoice;
			}
		});

		if (this.specifyPreferencesForm.get('apptCordinationRequired').value === '2') {
			delete this.requestReferralDetails.appt_prefs.date_time;
			delete this.requestReferralDetails.appt_prefs.transferEheRecords;
		}
	}

	/**
	 * Submits all request referral details added
	 */
	submitReferralInfo() {
		this.analyticsData.id = 'Submit referral request details';
		this.analyticsData.redirectedTo = '/wellness/referral/current/summary';
		this.analyticsData.placement = 'Request referral specify preferences';
		let locList = [];
		let dateTimeList = [];
		const specifyPreferencesBean = this.specifyPreferencesForm.getRawValue();

		dateTimeList = this.validateDateTime(specifyPreferencesBean);
		locList = this.validateLocations(specifyPreferencesBean);
		this.removeValidations(specifyPreferencesBean);

		// Checking if specialty belongs to medical group, if not invalidating plan name and type
		if (!this.checkSpecialty(2)) {
			this.specifyPreferencesForm.controls.insurance.get('planId').setErrors(null);
			this.specifyPreferencesForm.controls.insurance.get('planTypeId').setErrors(null);
		}

		if (this.specifyPreferencesForm.valid) {
			specifyPreferencesBean.date_time = dateTimeList;
			specifyPreferencesBean.location = locList;

			this.requestReferralDetails.appt_prefs = specifyPreferencesBean;

			this.spinner.startLoader();

			this.subscriptions.push(
				this.serviceRouter
					.makeRequest(
						this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
						this.config.url.referralApi.saveReferral,
						METHOD_TYPE.SECURED_POST,
						this.requestReferralDetails
					)
					.subscribe(
						response => {
							this.spinner.stopLoader();
							this.route.navigate(['wellness/referral/current/summary'], {
								queryParams: {
									appointmentRequestSent: true,
									data: this.encodedData,
								},
							});
						},
						error => {
							this.spinner.stopLoader();
						}
					)
			);
		}
	}
	// Formatting time values
	translate(value) {
		// Values to be displayed on Slider
		// If time is after 12, converting to 12 Hour format
		if (value >= 12) {
			// If number is not decimal, appending with '00'
			if (value % 1 === 0) {
				return value - 12 + ':00 PM';
			} else {
				// If number is decimal, appending with '30'
				return Math.floor(value) - 12 + ':30 PM';
			}
		} else {
			if (value % 1 === 0) {
				// If number is not decimal, appending with '00'
				return value + ':00 AM';
			} else {
				// If number is not decimal, appending with '00'
				return Math.floor(value) + ':30 AM';
			}
		}
	}

	/**
	 * Cancels and resets all request referral information added
	 */
	cancel() {
		this.analyticsData.id = 'cancel Request referral specify preferences';
		this.analyticsData.redirectedTo = '/wellness/referral/request';
		this.analyticsData.placement = 'Request referral specify preferences';
		this.resetEvent.emit(true);
		this.resetLocationArray();
		this.specifyPreferencesForm.reset();
		this.setDefaultValues();

		this.sliderTime.forEach(element => {
			element.value = this.value;
			element.highValue = this.highValue;
		});
		this.stepper.reset();
		// this.reset.emit(true);
	}

	/**
	 * Resets the location array on cancel request referral
	 */
	resetLocationArray() {
		this.canAddNewLocation = true;
		if (this.locationArr.length > 0) {
			this.resetArr();
		} else {
			return;
		}
	}

	/**
	 * Removes all values of location array
	 */
	resetArr() {
		this.removeLocation(this.locationArr.length - 1, '');
		this.resetLocationArray();
	}

	/**
	 * Sets default profile address values on cancel request referral
	 */
	setDefaultValues() {
		this.subscriptions.push(
			this.dataCarrier.getChannel().subscribe(data => {
				const profile = data.profile.profileInfo;
				this.city = profile.city;
				this.state = profile.state;
				this.zip = profile.zipCode;
			})
		);

		this.locationGroup({
			city: this.city,
			state: this.state,
			zip: this.zip,
			locationSelected: true,
		});
	}

	/**
	 * Dialog for adding a new location preference
	 */
	addLocationDialog(): void {
		this.analyticsData.id = 'Add provider preference location';
		this.analyticsData.redirectedTo = '/wellness/referral/request';
		this.analyticsData.placement = 'Request referral specify preferences';
		const dialogRef = this.dialog.open(AddLocationComponent, {
			width: '512px',
			data: {
				states: this.states,
			},
		});

		dialogRef.afterClosed().subscribe(result => {
			if (result) {
				this.locationGroup({
					city: result.city,
					state: result.state,
					zip: result.zip,
					locationSelected: true,
				});
			}
		});
		this.analyticsData.id = 'Add location';
		this.analyticsData.redirectedTo = '/wellness/referral/request';
		this.analyticsData.placement = 'Request referral Add location Modal';
		this.eheAnalyticsDirective.captureModalButtonData(this.analyticsData);
	}

	/**
	 * Removes an added location preference
	 */
	removeLocation(index, object): void {
		this.analyticsData.id = 'Remove provider preference location';
		this.analyticsData.redirectedTo = '/wellness/referral/request';
		this.analyticsData.placement = 'Request referral specify preferences';
		this.locationArr.splice(index, 1);
		if (this.locationArr.length <= 4) {
			this.canAddNewLocation = true;
		}
		this.removeLocationGroup(index);
	}

	/**
	 * Checking if specialty belongs to medical group, if not invalidating plan name and type
	 * @param index
	 */
	checkSpecialty(index) {
		if (this.requestReferralDetails && this.requestReferralDetails.referral) {
			for (const referral of this.requestReferralDetails.referral) {
				if (referral.specialty && referral.specialty.medicalGroup === index) {
					return true;
				}
				if (
					index === this.medicalFilter.medicalGroup &&
					(!referral.specialty.medicalGroup || referral.specialty.medicalGroup === 0)
				) {
					return true;
				}
			}
		}
	}

	/**
	 * Destroys component
	 */
	ngOnDestroy() {
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
		this.subscriptions = [];
	}
}
